import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { getAllChatMessage } from "../app/config/api/chatApis";
import { useSocketContext } from "../contexts/socketContext";
import { useSelector } from "react-redux";

const useSocket = () => {
  const {
    socket,
    isConnected,
    conversatioRooms,
    currentConversationId,
    setCurrentConversationId,
  } = useSocketContext();

  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const messagesPerPage = 10;
  const listenersSet = useRef();

  const currentUserId = useSelector((state) => {
    if (state?.auth?.user?.id) {
      return state.auth.user.id;
    } else {
      return null;
    }
  });

  const setupEventListeners = useCallback(() => {
    socket.on("conversation:error", (error) => {
      toast.error(
        `Conversation Error: ${error.message || "An unknown error occurred."}`
      );
    });

    socket.on("message:error", (error) => {
      toast.error(
        `Message Error: ${error.message || "An unknown error occurred."}`
      );
    });
    socket.on("message:receive", (data) => {
      setMessages((prev) => {
        if (!prev.some((msg) => msg.id === data.id)) {
          return [...prev, data];
        }
        return prev;
      });
    });

    socket.on("typing:start", (data) => {
      const filteredUserIds =
        data?.userIds?.filter((id) => id !== currentUserId) || [];
      setTypingUsers(filteredUserIds);
    });

    socket.on("typing:stop", (data) => {
      const filteredUserIds =
        data?.userIds?.filter((id) => id !== currentUserId) || [];

      setTypingUsers(filteredUserIds);
    });

    socket.on("message:send", () => {});

    listenersSet.current = true;
  }, [socket, currentConversationId]);

  useEffect(() => {
    if (socket && !listenersSet.current) {
      setupEventListeners();
    }
  }, [socket, setupEventListeners]);

  const joinConversation = useCallback(
    (workOrderConversationId) => {
      if (!socket || !isConnected || !workOrderConversationId) {
        return;
      }

      const convId = conversatioRooms?.find(
        (e) => e.entityId === workOrderConversationId
      );
      setCurrentConversationId(convId?.conversationId);
      fetchMessages(
        0,
        convId?.conversationId
          ? convId?.conversationId
          : workOrderConversationId
      );
    },
    [socket, isConnected, conversatioRooms]
  );

  const fetchMessages = useCallback(
    async (pageToFetch, conversationId) => {
      if (!conversationId || isLoading || !hasMore) return;

      setIsLoading(true);
      try {
        const offset = pageToFetch * messagesPerPage;
        const response = await getAllChatMessage(
          conversationId,
          offset,
          messagesPerPage
        );

        if (response?.messages) {
          const newMessages = response.messages.sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
          );

          setMessages((prev) =>
            pageToFetch === 0
              ? newMessages
              : [...newMessages, ...prev].filter(
                  (msg, index, self) =>
                    index === self.findIndex((m) => m.id === msg.id)
                )
          );
          setHasMore(response.messages.length === messagesPerPage);
          setPage(pageToFetch);
        }
      } catch (error) {
        toast.error("Failed to load messages.");
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, hasMore]
  );

  const sendMessage = useCallback(
    (messageData) => {
      if (socket && isConnected && messageData?.conversationId) {
        const payload = {
          conversationId: messageData.conversationId,
          text: messageData.text,
          metadata: { priority: "normal" },
        };
        socket.emit("message:send", payload);
      } else {
        console.warn(
          "Socket not connected or no conversation ID, cannot send message."
        );
      }
    },
    [socket, isConnected]
  );

  const startTyping = useCallback(
    (conversationId) => {
      if (socket && isConnected && conversationId) {
        const payload = { conversationId };

        socket.emit("typing:start", payload);
      }
    },
    [socket, isConnected]
  );
  const stopTyping = useCallback(
    (conversationId) => {
      if (socket && isConnected) {
        const payload = { conversationId };

        socket.emit("typing:stop", payload);
      }
    },
    [socket, isConnected]
  );

  useEffect(() => {
    return () => {
      if (socket && currentConversationId) {
        socket.emit("conversation:leave", {
          conversationId: currentConversationId,
        });
      }
    };
  }, [socket, currentConversationId]);

  return {
    currentConversationId,
    isConnected,
    messages,
    sendMessage,
    socket,
    startTyping,
    stopTyping,
    fetchMessages,
    joinConversation,
    page,
    isLoading,
    hasMore,
    typingUsers,
  };
};

export default useSocket;
